// addAppCrmLeads.js — the built app's "send sign-ups to CRM" opt-in on the
// /add-app builder (POB, Nika 2026-10-30 "CRM leads, opt-in per app").
//
// A site the workspace AI builder made can hand its sign-up form rows to the
// workspace CRM as leads — only when the app's owner turns it on. The server
// owns the whole decision (server BuiltAppCrmLeadsController):
//   GET   /core/workspaces/:workspaceId/built-apps/:projectId/crm-leads
//   PATCH same path { enabled }      — owner / admin only (403 otherwise)
// through the SDK's sdk.getBuiltAppCrmLeads / sdk.setBuiltAppCrmLeads. The
// answer: { projectId, enabled, signupForms, crmInstalled, active, reason,
// updatedAt }, reason null when active, else 'no_signup_form' |
// 'crm_not_installed' | 'off'.
//
// The row (pages/add-app.js CrmLeadsRow) keeps the answer in its OWN state
// (`crmLeads`, `crmLeadsFor`, `crmLeadsBusy`) — no root key. What it shows:
//   - nothing for a caller the server refuses (editor, non-member → 403), a
//     missing app (404), an SDK without the methods (an older published SDK)
//     or the null client: the row is hidden, no error;
//   - the switch only when the app declares at least one sign-up form;
//   - "New sign-ups go to your CRM" when active, else the reason's own line,
//     except 'off' — the switch's state says that one.
// A failed PATCH puts the switch back where it was and raises the kit's
// error toast (openNotification), as every other settings write here does.
// The workspace id is the shell's one resolver (el.call('activeWorkspaceId'),
// functions/scopedClient.js), reached through the functions barrel.

// The SDK the element reaches (el.sdk, else the shell's getSDK), or null for
// the null client. Every method of the shell's SDK answers a PROMISE (an
// init-await Proxy), so only the method's PRESENCE is read synchronously.
const _crmLeadsSdk = (el) => {
  let sdk = null
  try {
    sdk = (el && el.sdk) || (el && typeof el.call === 'function' ? el.call('getSDK') : null)
  } catch (_) {
    sdk = null
  }
  if (!sdk || sdk.isNullClient === true) return null
  return sdk
}

// The active workspace id through the shell's resolver, or null.
const _crmLeadsWorkspaceId = (el, root) => {
  try {
    return (el && typeof el.call === 'function' && el.call('activeWorkspaceId', root)) || null
  } catch (_) {
    return null
  }
}

// The server's answer, kept to the fields the row reads. Anything that is not
// an answer reads null (the row hides).
const _crmLeadsAnswer = (answer) => {
  if (!answer || typeof answer !== 'object') return null
  if (typeof answer.enabled !== 'boolean') return null
  return {
    projectId: answer.projectId != null ? String(answer.projectId) : '',
    enabled: answer.enabled,
    signupForms: Array.isArray(answer.signupForms) ? answer.signupForms : [],
    crmInstalled: answer.crmInstalled === true,
    active: answer.active === true,
    reason: typeof answer.reason === 'string' ? answer.reason : null,
    updatedAt: answer.updatedAt || null
  }
}

// PURE — what the row shows for an answer:
//   { show, toggle, checked, lineKey }
// lineKey is a literal polyglot key (never built), or '' for no line.
export function addAppCrmLeadsView(answer) {
  const a = _crmLeadsAnswer(answer)
  if (!a) return { show: false, toggle: false, checked: false, lineKey: '' }
  const toggle = a.signupForms.length > 0
  let lineKey = ''
  if (a.active) lineKey = 'workspace.addApp.crmLeads.on'
  else if (a.reason === 'crm_not_installed') lineKey = 'workspace.addApp.crmLeads.noCrm'
  else if (a.reason === 'no_signup_form') lineKey = 'workspace.addApp.crmLeads.noForm'
  return { show: toggle || !!lineKey, toggle, checked: a.enabled, lineKey }
}

// The row's own view, for its reactive props: el.call('addAppCrmLeadsRowView', s)
export function addAppCrmLeadsRowView(s) {
  const bound = !!(s && s.root && s.root._addAppProjectId)
  const forThis = bound && s && s.crmLeadsFor === String(s.root._addAppProjectId)
  return addAppCrmLeadsView(forThis ? s.crmLeads : null)
}

// Read the bound app's setting into the calling row's state. `this` is the row
// (el.call('addAppCrmLeadsLoad')). Answers the stored view, or null.
export async function addAppCrmLeadsLoad() {
  const el = this
  const s = el && el.state
  if (!s || typeof s.update !== 'function') return null
  const root = s.root || null
  const projectId = root && root._addAppProjectId ? String(root._addAppProjectId) : ''
  const workspaceId = _crmLeadsWorkspaceId(el, root)
  const sdk = _crmLeadsSdk(el)
  if (!projectId || !workspaceId || !sdk || typeof sdk.getBuiltAppCrmLeads !== 'function') {
    s.update({ crmLeads: null, crmLeadsFor: projectId, crmLeadsBusy: false })
    return null
  }
  if (s.crmLeadsFor !== projectId) {
    s.update({ crmLeads: null, crmLeadsFor: projectId, crmLeadsBusy: false })
  }
  let answer = null
  try {
    answer = _crmLeadsAnswer(await sdk.getBuiltAppCrmLeads(workspaceId, projectId))
  } catch (_) {
    // 403 (not an owner / admin), 404, offline: the row stays hidden
    answer = null
  }
  // the builder moved to another app while the read was in flight
  if (String((root && root._addAppProjectId) || '') !== projectId) return null
  s.update({ crmLeads: answer, crmLeadsFor: projectId })
  return answer
}

// Flip the switch. `this` is the switch or the row (el.call). The switch moves
// at once; the server's answer replaces it; a failure puts it back and raises
// the error toast.
export async function addAppCrmLeadsToggle() {
  const el = this
  const s = el && el.state
  if (!s || typeof s.update !== 'function') return null
  const root = s.root || null
  const projectId = root && root._addAppProjectId ? String(root._addAppProjectId) : ''
  const prev = s.crmLeadsFor === projectId ? s.crmLeads : null
  if (!prev || s.crmLeadsBusy) return null
  const workspaceId = _crmLeadsWorkspaceId(el, root)
  const sdk = _crmLeadsSdk(el)
  if (!workspaceId || !sdk || typeof sdk.setBuiltAppCrmLeads !== 'function') return null
  const enabled = !prev.enabled
  s.update({ crmLeadsBusy: true, crmLeads: { ...prev, enabled } })
  try {
    const answer = _crmLeadsAnswer(
      await sdk.setBuiltAppCrmLeads(workspaceId, projectId, { enabled })
    )
    s.update({ crmLeadsBusy: false, crmLeads: answer || { ...prev, enabled } })
    return answer
  } catch (_) {
    s.update({ crmLeadsBusy: false, crmLeads: prev })
    try {
      el.call('openNotification', {
        title: el.call('polyglot', 'workspace.addApp.crmLeads.saveError'),
        type: 'error'
      })
    } catch (__) {
      /* the switch is back where it was */
    }
    return null
  }
}
